/**
 * Wire types shared with the server.
 *
 * `BranchRequest`, `BranchSuggestion` and `CommandIntent` come from `@hc/shared` and are
 * imported, not restated. The three declarations below are envelope shapes that only the
 * API cares about, and the client cannot import from `@hc/server`, so they are mirrored
 * by hand from `packages/server/src/routes/schemas.ts` and `packages/server/src/llm/
 * generate.ts`. Both sides are owned together; a change is a change to two files.
 */

import type { BranchSuggestion, KeyEstimate, ModelChoice, Perspective } from '@hc/shared';

/**
 * What the server says about a request when it was asked to show its working.
 *
 * Present only when the request carried `debug: true`, which only happens while the
 * "Show model prompts" setting is on. Mirrored by hand from the server exactly as the
 * envelopes below are, and parsed defensively rather than trusted: this drives a
 * diagnostics sheet, so a field the server stops sending should cost that field and
 * nothing else.
 */
export interface LlmDebugPayload {
  /** The resolved provider model, not the tier name the request asked for. */
  model: string;
  system: string;
  user: string;
  /** Whether the answer came from the server's branch cache. The look-ahead evidence. */
  cached: boolean;
  paletteKinds: string[];
  ms: number;
  /** The reasoning effort the request ran at — the latency knob made visible. */
  effort?: string;
}

/** A `LlmDebugPayload` out of an untrusted body, or `null` if it is not one. */
export function parseDebugPayload(value: unknown): LlmDebugPayload | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (typeof record.model !== 'string') return null;
  return {
    model: record.model,
    system: typeof record.system === 'string' ? record.system : '',
    user: typeof record.user === 'string' ? record.user : '',
    cached: record.cached === true,
    paletteKinds: Array.isArray(record.paletteKinds)
      ? record.paletteKinds.filter((kind): kind is string => typeof kind === 'string')
      : [],
    ms: typeof record.ms === 'number' && Number.isFinite(record.ms) ? record.ms : 0,
    ...(typeof record.effort === 'string' ? { effort: record.effort } : {}),
  };
}

/**
 * Where a suggestion came from.
 *
 * `fallback` means the model was unavailable and this is a locally-derived continuation
 * from the server's static table. The UI should style these quietly: they are honest
 * padding rather than the product working well.
 */
export type SuggestionOrigin = 'model' | 'repair' | 'fallback';

/** One NDJSON line from `POST /api/branches`. */
export type BranchStreamEvent =
  | { type: 'suggestion'; suggestion: BranchSuggestion; origin: SuggestionOrigin }
  | {
      type: 'done';
      status: 'ready' | 'partial' | 'fallback';
      cached: boolean;
      /** Only when the request asked for it. */
      debug?: LlmDebugPayload;
    }
  | { type: 'error'; message: string };

/**
 * The two fields every generating route accepts.
 *
 * `/api/branches` carries them on `BranchRequest` (a frozen shape in `@hc/shared`); the
 * two routes whose bodies are declared here get them from this.
 */
export interface LlmRequestOptions {
  /** The player's model tier. Absent means the server's default. */
  modelChoice?: ModelChoice;
  /** Ask the response to carry the composed prompt. Only while the setting is on. */
  debug?: boolean;
}

/** Body of `POST /api/explain`. */
export interface ExplainRequest extends LlmRequestOptions {
  target: 'suggestion' | 'transition' | 'current-chord';
  fromSymbol?: string;
  toSymbol?: string;
  /** The offered branch, when the player tapped one. */
  suggestion?: BranchSuggestion;
  keyEstimate?: KeyEstimate;
  perspectives?: Perspective[];
  intention?: string;
  /** A specific question, when the player asked one rather than just tapping. */
  question?: string;
}

/** Body of `POST /api/command`. */
export interface CommandRequest extends LlmRequestOptions {
  utterance: string;
  perspectives?: Perspective[];
  adventurousness?: number;
  currentSymbol?: string;
  intention?: string;
}

/** Body of `GET /api/health`. */
export interface HealthResponse {
  ok: boolean;
  llm: 'ok' | 'degraded' | 'down';
  uptimeSeconds: number;
}

/** Options every call accepts. `fetchImpl` exists so tests never touch the network. */
export interface ApiOptions {
  signal?: AbortSignal;
  /** Defaults to `''` — same origin, which is how this is always deployed. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/** Thrown when the server rejects a request outright, before any body has streamed. */
export class ApiError extends Error {
  readonly status: number;
  readonly issues: string[];

  constructor(status: number, message: string, issues: string[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = issues;
  }
}

/** Shared request plumbing. Not exported from the package index — an implementation detail. */
export async function postJson(
  path: string,
  body: unknown,
  options: ApiOptions | undefined,
  accept: string,
): Promise<Response> {
  const doFetch = options?.fetchImpl ?? fetch;
  const response = await doFetch(`${options?.baseUrl ?? ''}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: accept },
    body: JSON.stringify(body),
    ...(options?.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) {
    const detail = await readErrorDetail(response);
    throw new ApiError(response.status, detail.error, detail.issues);
  }
  return response;
}

async function readErrorDetail(response: Response): Promise<{ error: string; issues: string[] }> {
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === 'object') {
      const record = parsed as { error?: unknown; issues?: unknown };
      return {
        error:
          typeof record.error === 'string' ? record.error : `request failed (${response.status})`,
        issues: Array.isArray(record.issues)
          ? record.issues.filter((i) => typeof i === 'string')
          : [],
      };
    }
  } catch {
    // A non-JSON error body is not itself an error worth reporting.
  }
  return { error: `request failed (${response.status})`, issues: [] };
}

/**
 * Turn a byte stream into whole lines.
 *
 * Chunk boundaries land anywhere, including mid-character in a multi-byte sequence — and
 * the branch descriptions are full of ♭ and ♯. `TextDecoder` with `{ stream: true }` is
 * what makes that safe.
 */
export async function* readLines(response: Response): AsyncGenerator<string> {
  const body = response.body;
  if (!body) return;

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newline = buffer.indexOf('\n');
      while (newline >= 0) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (line !== '') yield line;
        newline = buffer.indexOf('\n');
      }
    }
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail !== '') yield tail;
  } finally {
    reader.releaseLock();
  }
}
